import { Button, Input, Typography } from "antd";
import { useCallback, useEffect, useState } from "react";
import {
  getHolidayRequestNotificationConfig,
  updateHolidayRequestNotificationConfig,
} from "../../utils/generalConfigurationService";
import { Notify } from "../../utils/constants";

const EMAIL_PATTERN = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

const parseEmailInputs = (values: string[]): string[] => {
  const normalizedEmails = values
    .map((email) => email.trim().toLowerCase())
    .filter(Boolean);

  return Array.from(new Set(normalizedEmails));
};

const HolidayRequestNotificationSection = () => {
  const [emailInputs, setEmailInputs] = useState<string[]>([""]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const loadConfiguration = useCallback(async () => {
    setIsLoading(true);
    try {
      const config = await getHolidayRequestNotificationConfig();
      const configuredEmails = config.ccEmails || [];
      setEmailInputs(configuredEmails.length ? configuredEmails : [""]);
    } catch (error) {
      console.error("Failed to load holiday request notification emails:", error);
      Notify("error", "Could not load holiday request notification emails.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfiguration();
  }, [loadConfiguration]);

  const handleEmailChange = (index: number, value: string) => {
    setEmailInputs((previousInputs) =>
      previousInputs.map((input, inputIndex) =>
        inputIndex === index ? value : input,
      ),
    );
  };

  const handleAddEmailInput = () => {
    setEmailInputs((previousInputs) => [...previousInputs, ""]);
  };

  const handleRemoveEmailInput = (index: number) => {
    setEmailInputs((previousInputs) => {
      if (previousInputs.length === 1) {
        return [""];
      }

      return previousInputs.filter((_, inputIndex) => inputIndex !== index);
    });
  };

  const handleSave = async () => {
    const emails = parseEmailInputs(emailInputs);
    if (!emails.length) {
      Notify("error", "Please provide at least one email address.");
      return;
    }

    const invalidEmail = emails.find((email) => !EMAIL_PATTERN.test(email));
    if (invalidEmail) {
      Notify("error", `Invalid email address: ${invalidEmail}`);
      return;
    }

    setIsSaving(true);
    try {
      const updatedConfig = await updateHolidayRequestNotificationConfig(emails);
      const updatedEmails = updatedConfig.ccEmails || [];
      setEmailInputs(updatedEmails.length ? updatedEmails : [""]);
      Notify("success", "Holiday request notification emails updated successfully.");
    } catch (error) {
      console.error("Failed to update holiday request notification emails:", error);
      Notify("error", "Could not update holiday request notification emails.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="section-container" style={{ marginTop: 16 }}>
      <Typography.Title level={4}>Holiday Request Notification Emails</Typography.Title>
      <Typography.Paragraph type="secondary">
        Add email addresses using the button below.
      </Typography.Paragraph>
      {emailInputs.map((emailInput, index) => (
        <div
          key={`email-input-${index}`}
          style={{ display: "flex", gap: 8, marginBottom: 8 }}
        >
          <Input
            value={emailInput}
            onChange={(event) => handleEmailChange(index, event.target.value)}
            placeholder="name@example.com"
            disabled={isLoading || isSaving}
          />
          <Button
            onClick={() => handleRemoveEmailInput(index)}
            disabled={isLoading || isSaving || emailInputs.length === 1}
          >
            Remove
          </Button>
        </div>
      ))}
      <Button
        onClick={handleAddEmailInput}
        disabled={isLoading || isSaving}
      >
        Add email
      </Button>
      <Button
        type="primary"
        style={{ marginTop: 12, marginLeft: 8 }}
        onClick={handleSave}
        loading={isSaving}
        disabled={isLoading}
      >
        Save notification emails
      </Button>
    </div>
  );
};

export default HolidayRequestNotificationSection;
